import { reportDisplay } from './reportNumbers';
// Export every supplied row, independent of the DataGrid's page size or viewport.
const BATCH_SIZE = 5000;
const EXCEL_DATA_ROWS = 1048575; // Reserve one row per sheet for column headings.
const yieldToBrowser = () => new Promise((resolve) => setTimeout(resolve, 0));

export const getExportColumns = (columns) => columns.filter(
  (column) => column.exportable !== false && column.disableExport !== true
);

export const getExportValue = (row, column) => {
  if (typeof column.exportValue === 'function') return reportDisplay(column.exportValue(row));
  let value = row[column.field];
  if (typeof column.valueGetter === 'function') {
    value = column.valueGetter(value, row, column);
  }
  if (typeof column.valueFormatter === 'function') {
    value = column.valueFormatter(value, row, column);
  }
  return reportDisplay(value);
};

const normalizeValue = (value) => {
  if (value == null) return '';
  if (value instanceof Date) return value.toISOString();
  if (typeof value === 'number') return Number.isFinite(value) ? value : '';
  if (typeof value === 'object') return JSON.stringify(value);
  return value;
};

export const createExcelWorkbook = async (rows, columns, XLSX) => {
  const selected = getExportColumns(columns);
  if (!selected.length) throw new Error('No exportable columns are available.');
  if (selected.length > 16384) throw new Error('Excel supports at most 16,384 columns. Reduce the number of exported columns.');
  const workbook = XLSX.utils.book_new();
  const headers = selected.map(column => String(column.headerName || column.field));
  const sheetCount = Math.max(1, Math.ceil(rows.length / EXCEL_DATA_ROWS));
  for (let index = 0; index < sheetCount; index += 1) {
    const sheet = XLSX.utils.aoa_to_sheet([headers]);
    const start = index * EXCEL_DATA_ROWS;
    const end = Math.min(start + EXCEL_DATA_ROWS, rows.length);
    for (let offset = start; offset < end; offset += BATCH_SIZE) {
      const batch = rows.slice(offset, Math.min(offset + BATCH_SIZE, end)).map(row =>
        selected.map(column => normalizeValue(getExportValue(row, column)))
      );
      // AOA creates string cells for text, including text beginning with '='; no formulas are generated.
      XLSX.utils.sheet_add_aoa(sheet, batch, { origin: -1 });
      await yieldToBrowser();
    }
    sheet['!cols'] = headers.map(header => ({ wch: Math.min(40, Math.max(14, header.length + 2)) }));
    sheet['!autofilter'] = { ref: sheet['!ref'] };
    XLSX.utils.book_append_sheet(workbook, sheet, index ? `Report ${index + 1}` : 'Report');
  }
  return workbook;
};

// Match exportTableToPDF's options so Table.js can call either exporter directly.
export const exportTableToExcel = async ({ rows = [], columns = [], title = 'Report', fileName, notes = [] } = {}) => {
  const unsafeFileNameCharacters = '<>:"/\\|?*';
  const name = Array.from((fileName || title || 'Report').replace(/\.xlsx$/i, ''))
    .map(character => character.charCodeAt(0) < 32 || unsafeFileNameCharacters.includes(character) ? '_' : character)
    .join('')
    .trim() || 'Report';
  const XLSX = await import('xlsx');
  const workbook = await createExcelWorkbook(rows, columns, XLSX);
  // Keep the data sheet sortable and record date/filter context on a separate details sheet.
  if (notes.length) {
    const details = XLSX.utils.aoa_to_sheet([[title], ...notes.map(note => [String(note)])]);
    details['!cols'] = [{ wch: 100 }];
    XLSX.utils.book_append_sheet(workbook, details, 'Report Details');
  }
  const blob = new Blob([XLSX.write(workbook, { bookType: 'xlsx', type: 'array', compression: true })], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `${name}.xlsx`;
  document.body.appendChild(anchor);
  try {
    anchor.click();
  } finally {
    anchor.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
};
